import { API_BASE_URL, DEFAULT_LANGUAGE, DEFAULT_ID_WL } from "@/lib/constants";

export interface ApiResponse<T = unknown> {
  status: 0 | 1;
  message: string;
  data: T;
}

export class ApiError extends Error {
  statusCode: number;
  constructor(statusCode: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
  }
}

/* External auth configuration (used when embedded in great-r3-app) */

interface ApiClientConfig {
  getAuth: () => {
    token: string | null;
    idAccount: string | null;
    language: string;
    idWl: string;
  };
  onLogout: () => void;
  baseUrl?: string;
}

let _externalConfig: ApiClientConfig | null = null;

export function configureApiClient(config: ApiClientConfig) {
  _externalConfig = config;
}

/* Internal fallback (standalone mode) */

// Lazy reference to avoid bundling require() in production
let _authStoreRef: (() => ReturnType<typeof import("@/stores/auth-store")["useAuthStore"]["getState"]>) | null = null;

export function setAuthStoreRef(getter: typeof _authStoreRef) {
  _authStoreRef = getter;
}

function getAuthFromStore() {
  if (_authStoreRef) return _authStoreRef();
  throw new Error("Auth store not configured. Call configureApiClient() or setAuthStoreRef() first.");
}

function getHeaders(): HeadersInit {
  const token = _externalConfig
    ? _externalConfig.getAuth().token
    : getAuthFromStore().token;

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function getBaseUrl(withAccount = true): string {
  const baseApiUrl = _externalConfig?.baseUrl || API_BASE_URL;

  const auth = _externalConfig
    ? _externalConfig.getAuth()
    : getAuthFromStore();

  const lang = auth.language || DEFAULT_LANGUAGE;
  const wl = auth.idWl || DEFAULT_ID_WL;

  if (withAccount && auth.idAccount) {
    return `${baseApiUrl}/v1/${lang}/${wl}/accounts/${auth.idAccount}`;
  }
  return `${baseApiUrl}/v1/${lang}/${wl}`;
}

function handleLogout() {
  if (_externalConfig) {
    _externalConfig.onLogout();
  } else {
    getAuthFromStore().logout();
  }
}

async function handleResponse<T>(res: Response): Promise<ApiResponse<T>> {
  if (res.status === 204)
    return { status: 1, message: "No content", data: [] as unknown as T };

  if (res.status === 401) {
    handleLogout();
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text);
  }

  return res.json();
}

export const api = {
  async get<T>(
    path: string,
    params?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    const url = new URL(`${getBaseUrl()}${path}`);
    if (params) {
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    }
    const res = await fetch(url.toString(), { headers: getHeaders() });
    return handleResponse<T>(res);
  },

  async post<T>(path: string, body: unknown): Promise<ApiResponse<T>> {
    const res = await fetch(`${getBaseUrl()}${path}`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(body),
    });
    return handleResponse<T>(res);
  },

  async put<T>(path: string, body: unknown): Promise<ApiResponse<T>> {
    const res = await fetch(`${getBaseUrl()}${path}`, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(body),
    });
    return handleResponse<T>(res);
  },

  async del<T>(path: string): Promise<ApiResponse<T>> {
    const res = await fetch(`${getBaseUrl()}${path}`, {
      method: "DELETE",
      headers: getHeaders(),
    });
    return handleResponse<T>(res);
  },

  async getAccounts<T>(): Promise<ApiResponse<T>> {
    const res = await fetch(`${getBaseUrl(false)}/accounts`, {
      headers: getHeaders(),
    });
    return handleResponse<T>(res);
  },
};
